// deps
import React, { useMemo, useContext, useCallback, memo, useRef } from "react";

// internal deps
import { CartItemWithUUID, CrossSellTabType } from "@swiggy-private/cart-types";
import { CrossSellM2Carousel, CrossSellM2ItemsAdded, MAGIC_ITEMS_TYPE } from "@swiggy-private/cart-ui";
import { ErrorBoundary } from "@swiggy-private/core-ui";
import { getNRErrorInfo, isEmpty } from "@swiggy-private/js-utils";

// contexts
import { CartContext } from "@cart-client/contexts/CartContext";
import { UpdateCartContext } from "@cart-client/contexts/UpdateCartContext";
import { AnalyticsContext, getBridgeSdk, NEWRELIC_TABLES, recordNREvent } from "@swiggy-private/checkout-core";
import { CartFlowContext } from "@cart-client/contexts/CartFlowContext";

// helpers
import { createCartItemListWithUUID } from "@cart-client/helpers/CartItemRenderer";
import { getCrossSellPocketHeroNudge, getMenuItems, getRestaurantId, getSessionId } from "@cart-client/helpers/Cart";
import { NUDGE_CART_CATEGORY_TYPE } from "@cart-client/constants/CartDeliverySpeed";
import {
    filterLocalAndServerCartFromPayload,
    getMROChildCartByRestaurantId,
    isMROFlow,
} from "@cart-client/helpers/MROFoodCart";

// interfaces
import { ReviewedCart } from "@cart-client/interfaces/Cart";
import {
    CarouselItemDetailsParams,
    ClickAddItemCarouselParams,
    ClickItemDetailsCarouselParams,
    impressionCartDeliverySpeedNudge,
    impressionMagicItemsFtue,
    impressionMagicItemsTimer,
    impressionMagicItemsEmpty,
    PriceInfo,
    WidgetInfo,
} from "@cart-client/analytics/events";
import { getCrossSellSuggestions } from "@cart-client/helpers/CrossSellM2";
import { useCartItemActions } from "@cart-client/hooks/useCartItemActions";
import { useCartRendererAnalytics } from "@cart-client/hooks/useCartRenderedAnalytics";
import { VibrateAmplitudeTypes } from "@swiggy-private/native-bridge";
import { useCartRendererXP } from "@cart-client/hooks/useCartRendererXP";
import { MROReviewedCart } from "@cart-client/interfaces/MROFoodCart";

interface CrossSellM2ItemRendererProps {
    onImpressionCrossSellM2Item: (params: {
        item: string;
        cart: string;
        basePrice?: number;
        slashedPrice?: number;
        position?: string;
    }) => void;
    onImpressionAddItemCarousel: (
        items: string[],
        cartId: string,
        selectedTab?: CrossSellTabType | null,
        widgetInfo?: WidgetInfo,
    ) => void;
    onClickAddItemCarousel: (params: ClickAddItemCarouselParams) => void;
    onClickItemDetailsCarousel: (params: ClickItemDetailsCarouselParams) => void;
    onImpressionCarouselItemDetailsPopup?: (params: CarouselItemDetailsParams) => void;
    onImpressionCartItemDetails?: (itemId: string) => void;
    onImpressionMagicItemsUnlockPopup?: VoidFunction;
    onClickDismissMagicItemsUnlockPopup?: VoidFunction;
    onClickExploreMenuMagicItems?: VoidFunction;
    goToMenu?: (restaurantId?: string | number) => void;
    restaurantId?: string;
}

const CrossSellM2ItemRenderer = ({
    onImpressionCrossSellM2Item,
    onImpressionAddItemCarousel,
    onClickAddItemCarousel,
    onClickItemDetailsCarousel,
    onImpressionCarouselItemDetailsPopup,
    onImpressionCartItemDetails,
    onImpressionMagicItemsUnlockPopup,
    onClickDismissMagicItemsUnlockPopup,
    onClickExploreMenuMagicItems,
    goToMenu,
    restaurantId,
}: CrossSellM2ItemRendererProps) => {
    const currentCartIdRef = useRef("");
    const { impressionEvent } = useContext(AnalyticsContext);
    const { reloadCart, updateCartInProgress } = useContext(UpdateCartContext);
    const { data } = useContext(CartFlowContext);
    const isMROCart = isMROFlow(data?.cartSource);

    const {
        cartState: { localCart: localCartState, reviewedCart: reviewCartState },
    } = useContext(CartContext);

    const { localCart, reviewedCart } = useMemo(() => {
        return filterLocalAndServerCartFromPayload({ isMROCart, localCartState, reviewCartState, restaurantId });
    }, [isMROCart, localCartState, reviewCartState, restaurantId]);

    const { onClickChangeTabCarousel } = useCartRendererAnalytics(reviewedCart);

    const crossSellM2SuggestionsState = useMemo(() => getCrossSellSuggestions(reviewedCart), [reviewedCart]);

    const cartItemsList = useMemo(
        () => createCartItemListWithUUID(getMenuItems(reviewedCart as ReviewedCart)),
        [reviewedCart],
    );

    const restaurantDetails = useMemo(() => {
        if (isMROCart && !isEmpty(restaurantId)) {
            const childCart = getMROChildCartByRestaurantId(reviewCartState as MROReviewedCart, restaurantId);
            return childCart?.restaurant_details;
        }
        return reviewedCart?.data?.restaurant_details;
    }, [isMROCart, restaurantId, reviewCartState, reviewedCart]);

    const { addItem: addItemAction, onItemCustomisation: onItemCustomisationAction } = useCartItemActions({
        cartItemsList,
        restaurantDetails,
        source: "cross-sell-m2-item-renderer",
        dishStyle: reviewedCart?.data?.menu_items_widget_presentation_data?.dish_style,
        restaurantId,
    });

    const { isSnaccStorefrontV2 } = useCartRendererXP(reviewedCart);

    const crossSellNudge = useMemo(() => getCrossSellPocketHeroNudge(reviewedCart), [reviewedCart]);
    const rxId = useMemo(() => restaurantId || getRestaurantId(reviewedCart), [reviewedCart, restaurantId]);
    const sid = useMemo(() => getSessionId(), []);
    const presentationInfo = crossSellM2SuggestionsState?.crossSellHeader?.presentationInfo;
    const isMagicItems = useMemo(
        () => presentationInfo?.widget_properties_info?.type === MAGIC_ITEMS_TYPE,
        [presentationInfo?.widget_properties_info?.type],
    );
    const timerShown = useMemo(() => !isEmpty(presentationInfo?.timer_data), [presentationInfo?.timer_data]);

    const impressionAddItemCarousel = useCallback(
        (items: string[], selectedTab?: CrossSellTabType | null, widgetInfo?: WidgetInfo) => {
            if (localCart?.cart_id && String(localCart?.cart_id) !== currentCartIdRef.current && !isEmpty(items)) {
                onImpressionAddItemCarousel(items, String(localCart?.cart_id), selectedTab, widgetInfo);
                // Updating the ref here so that we always have the current cart id when cross sell carousel is shown
                currentCartIdRef.current = String(localCart?.cart_id);
            }
        },
        [onImpressionAddItemCarousel, localCart?.cart_id],
    );

    const impressionCrossSellM2Item = useCallback(
        (item: string, basePrice?: number, slashedPrice?: number, position?: string) => {
            onImpressionCrossSellM2Item({ item, cart: currentCartIdRef.current, basePrice, slashedPrice, position });
        },
        [onImpressionCrossSellM2Item],
    );

    const clickItemDetailsCarousel = useCallback(
        (itemId: string, position: string, selectedTab?: CrossSellTabType | null, priceInfo?: PriceInfo) => {
            onClickItemDetailsCarousel({
                itemId,
                cartId: String(localCart?.cart_id),
                position,
                selectedTab,
                priceInfo,
            });
        },
        [onClickItemDetailsCarousel, localCart?.cart_id],
    );

    const impressionCarouselItemDetailsPopup = useCallback(
        (itemId: string, position: string, selectedTab?: CrossSellTabType | null) => {
            onImpressionCarouselItemDetailsPopup?.({
                itemId,
                cartId: String(localCart?.cart_id),
                position,
                selectedTab,
            });
            onImpressionCartItemDetails?.(itemId);
        },
        [onImpressionCarouselItemDetailsPopup, onImpressionCartItemDetails, localCart?.cart_id],
    );

    const onError = useCallback(
        (error?: Error, errorInfo?: React.ErrorInfo) => {
            const errorEvents = getNRErrorInfo(error, errorInfo);
            recordNREvent({
                eventType: NEWRELIC_TABLES.CHECKOUT_WEB_ERROR_BOUNDARY,
                widgetType: "CROSS_SELL_M2_ITEM_RENDERER",
                isMROCart: String(isMROCart),
                restaurantId: String(restaurantId),
                ...errorEvents,
            });
        },
        [isMROCart, restaurantId],
    );

    const onPHNudgeImpression = useCallback(() => {
        impressionCartDeliverySpeedNudge({
            impressionEvent,
            cartId: localCart?.cart_id,
            addressId: localCart?.selectedAddress?.id,
            restaurantId: localCart?.restaurantId,
            source: NUDGE_CART_CATEGORY_TYPE.POCKET_HERO,
        });
    }, [impressionEvent, localCart?.cart_id, localCart?.restaurantId, localCart?.selectedAddress?.id]);

    const onItemCustomisation = useCallback(
        async (item: CartItemWithUUID, selectedTab?: CrossSellTabType | null, position?: string): Promise<void> => {
            onItemCustomisationAction(item, true);
            onClickAddItemCarousel({
                itemId: String(item.menu_item_id),
                cartId: String(localCart?.cart_id),
                isCustomizable: true,
                selectedTab,
                position,
                priceInfo: {
                    basePrice: item.final_price,
                    slashedPrice: item.base_price,
                },
            });
        },
        [onItemCustomisationAction, onClickAddItemCarousel, localCart?.cart_id],
    );

    const addItem = useCallback(
        async (item: CartItemWithUUID, selectedTab?: CrossSellTabType | null, position?: string): Promise<void> => {
            onClickAddItemCarousel({
                itemId: String(item.menu_item_id),
                cartId: String(localCart?.cart_id),
                isCustomizable: false,
                selectedTab,
                position,
                priceInfo: {
                    basePrice: item.subtotal,
                    slashedPrice: item.final_price,
                },
            });
            addItemAction(item);
        },
        [onClickAddItemCarousel, localCart?.cart_id, addItemAction],
    );

    const clickChangeTabCarousel = useCallback(
        (selectedTab: CrossSellTabType | null) => {
            // haptic feedback on tab change
            getBridgeSdk()
                .vibrate({ duration: 30, amplitude: VibrateAmplitudeTypes.LIGHT })
                .catch(() => void 0);

            onClickChangeTabCarousel(String(localCart?.cart_id), selectedTab);
        },
        [onClickChangeTabCarousel, localCart?.cart_id],
    );

    const onImpressionMagicItemsFtue = useCallback(() => {
        if (localCart?.cart_id) {
            impressionMagicItemsFtue(impressionEvent, String(localCart.cart_id));
        }
    }, [impressionEvent, localCart?.cart_id]);

    const onImpressionMagicItemsTimer = useCallback(
        (nearExpiry: boolean) => {
            if (localCart?.cart_id) {
                impressionMagicItemsTimer(impressionEvent, {
                    cartId: String(localCart.cart_id),
                    nearExpiry,
                    timerShown,
                });
            }
        },
        [impressionEvent, localCart?.cart_id, timerShown],
    );

    const onImpressionMagicItemsEmpty = useCallback(
        (savingsText?: string) => {
            if (localCart?.cart_id) {
                impressionMagicItemsEmpty(impressionEvent, String(localCart.cart_id), savingsText);
            }
        },
        [impressionEvent, localCart?.cart_id],
    );

    // Navigate to menu callback for magic items unlock popup
    // Reuses the same goToMenu callback as the "+ Add Items" button for consistent UX
    const handleExploreMenuClick = useCallback(() => {
        goToMenu?.(restaurantId);
    }, [goToMenu, restaurantId]);

    if (isEmpty(crossSellM2SuggestionsState.suggestions) && !crossSellM2SuggestionsState.allCrossSellSuggestionsAdded) {
        // Updating the ref here so that we always have the current cart id even when cross sell carousel isn't shown
        currentCartIdRef.current = String(localCart?.cart_id);
        return null;
    }

    if (crossSellM2SuggestionsState.allCrossSellSuggestionsAdded && !isMagicItems) {
        // Updating the ref here so that we always have the current cart id even when cross sell carousel isn't shown
        currentCartIdRef.current = String(localCart?.cart_id);
        return (
            <ErrorBoundary errorContent={null} errorFallback={onError}>
                <CrossSellM2ItemsAdded />
            </ErrorBoundary>
        );
    }

    return (
        <ErrorBoundary errorContent={null} errorFallback={onError}>
            <CrossSellM2Carousel
                crossSellM2ItemsList={crossSellM2SuggestionsState.suggestions}
                crossSellHeader={crossSellM2SuggestionsState?.crossSellHeader}
                rxId={rxId}
                sid={sid}
                onItemCustomisation={onItemCustomisation}
                addItem={addItem}
                onImpressionAddItemCarousel={impressionAddItemCarousel}
                onClickChangeTabCarousel={clickChangeTabCarousel}
                onClickItemDetailsCarousel={clickItemDetailsCarousel}
                onImpressionCrossSellM2Item={impressionCrossSellM2Item}
                onImpressionCarouselItemDetailsPopup={impressionCarouselItemDetailsPopup}
                PHNudge={crossSellNudge}
                onPHNudgeImpression={onPHNudgeImpression}
                defaultTabSelected={crossSellM2SuggestionsState.defaultTabSelected}
                allCrossSellSuggestionsAddedText={crossSellM2SuggestionsState.allCrossSellSuggestionsAddedText}
                allCrossSellSuggestionsAdded={crossSellM2SuggestionsState.allCrossSellSuggestionsAdded}
                onImpressionMagicItemsFtue={onImpressionMagicItemsFtue}
                onImpressionMagicItemsTimer={onImpressionMagicItemsTimer}
                onImpressionMagicItemsEmpty={onImpressionMagicItemsEmpty}
                onImpressionMagicItemsUnlockPopup={onImpressionMagicItemsUnlockPopup}
                onClickDismissMagicItemsUnlockPopup={onClickDismissMagicItemsUnlockPopup}
                onClickExploreMenuMagicItems={onClickExploreMenuMagicItems}
                onExploreMenuClick={handleExploreMenuClick}
                onTimerExpired={reloadCart}
                updateCartInProgress={updateCartInProgress}
                isSnaccStorefrontV2={isSnaccStorefrontV2}
            />
        </ErrorBoundary>
    );
};

export default memo(CrossSellM2ItemRenderer);
